import api from './client';
import { getToken } from './auth';

// 桌面端模型管家：Ollama 检测/安装/拉取/硬件分档推荐（后端 desktop_setup，仅桌面端）

export interface OllamaStatus {
  binary: boolean;
  running: boolean;
  models: string[];
  required: Record<string, boolean>;
  ready: boolean;
}

export interface OllamaCatalogModel {
  id: string;
  ollama_model: string;
  name: string;
  desc: string;
  size_bytes: number;
  min_ram_gb: number;
  resident: boolean;
  installed: boolean;
}

export interface OllamaModelsResponse {
  models: OllamaCatalogModel[];
  recommended: string | null;
  embed_model: string;
  embed_installed: boolean;
}

export interface SetupSseEvent {
  status: string;
  message?: string;
  model?: string;
  completed?: number;
  total?: number;
  digest?: string;
}

export const getOllamaStatus = () =>
  api.get<OllamaStatus>('/api/v1/setup/ollama-status').then((r) => r.data);

export const getOllamaModels = (ramGb: number) =>
  api.get<OllamaModelsResponse>('/api/v1/setup/ollama-models', { params: { ram_gb: ramGb } }).then((r) => r.data);

export const startOllama = () =>
  api.post<{ started: boolean; already: boolean }>('/api/v1/setup/ollama-start').then((r) => r.data);

/** 后台预热常驻模型（0.5b 对话 + 嵌入），调用方无需等待结果 */
export const warmOllama = () =>
  api.post<{ warming: boolean }>('/api/v1/setup/ollama-warm').then((r) => r.data);

/** POST SSE 流消费：逐事件回调；非 2xx（如 403 会员门）抛错并尽量带上后端 detail */
async function consumeSse(
  url: string,
  params: Record<string, string>,
  onEvent: (e: SetupSseEvent) => void
): Promise<void> {
  const token = getToken();
  const qs = new URLSearchParams(params).toString();
  const resp = await fetch(qs ? `${url}?${qs}` : url, {
    method: 'POST',
    headers: { Authorization: token ? `Bearer ${token}` : '' },
  });
  if (!resp.ok) {
    let detail = `请求失败（${resp.status}）`;
    try {
      const data = await resp.json();
      // 后端全局异常包={"error":{"message"}}（HTTPException 也被改写，无 detail 键），两边都认
      const msg = data?.detail ?? data?.error?.message;
      if (typeof msg === 'string' && msg) detail = msg;
    } catch {
      // ignore
    }
    const err: any = new Error(detail);
    err.status = resp.status;
    throw err;
  }
  const reader = resp.body?.getReader();
  if (!reader) return;
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith('data: ')) continue;
      try {
        onEvent(JSON.parse(line.slice(6)));
      } catch {
        // 忽略畸形帧
      }
    }
  }
}

export const installOllama = (onEvent: (e: SetupSseEvent) => void) =>
  consumeSse('/api/v1/setup/ollama-install', {}, onEvent);

export const pullOllamaModel = (model: string, onEvent: (e: SetupSseEvent) => void) =>
  consumeSse('/api/v1/setup/ollama-pull', { model }, onEvent);
